import styled from "styled-components";

export const Page = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 160px 40px 80px;
  color: var(--text-color);

  @media (max-width: 768px) {
    padding: 120px 20px 60px;
  }
`;

export const HeroWrap = styled.div`
  margin-bottom: 60px;
`;

export const Label = styled.span`
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  display: block;
  margin-bottom: 20px;
`;

export const Headline = styled.h1`
  font-size: clamp(3rem, 7vw, 5.5rem);
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -0.03em;
  margin: 0 0 40px 0;
  max-width: 900px;
  color: var(--text-color);
`;

export const SubtitleRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 60px;
  border-top: 1px solid rgba(150, 150, 150, 0.2);
  padding-top: 40px;
  color: var(--text-color);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  p {
    font-size: 1.2rem;
    line-height: 1.6;
    opacity: 0.9;
    margin: 0;
  }

  .meta {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;

    @media (max-width: 768px) {
      align-items: flex-start;
      text-align: left;
    }

    strong {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-top: 20px;
      opacity: 0.6;
      
      &:first-child {
        margin-top: 0;
      }
    }

    span {
      font-size: 1rem;
      font-weight: 700;
      margin-top: 4px;
    }
  }
`;

export const HeroImage = styled.div`
  width: 100%;
  margin-bottom: 120px;
  
  img, video {
    width: 100%;
    aspect-ratio: 16/7;
    object-fit: cover;
    border-radius: 4px;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  border-bottom: 1px solid rgba(150, 150, 150, 0.2);
  padding-bottom: 16px;
  margin-bottom: 60px;

  h2 {
    font-size: 2.5rem;
    font-weight: 900;
    margin: 0;
    letter-spacing: -1px;
  }

  span {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 2px;
    opacity: 0.5;
    color: var(--accent);
    text-transform: uppercase;
  }
`;

export const ChallengeSection = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 80px;
  margin-bottom: 120px;
  color: var(--text-color);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  h2 {
    font-size: 2.5rem;
    font-weight: 900;
    margin: 0 0 30px 0;
    letter-spacing: -1px;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    opacity: 0.8;
  }

  .metrics-box {
    display: flex;
    flex-direction: column;
    gap: 20px;

    .row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }
  }
`;

export const MetricCard = styled.div`
  background: var(--panel-bg);
  padding: 30px;
  border-radius: 4px;
  border: 1px solid rgba(150, 150, 150, 0.1);
  color: var(--text-color);

  h3 {
    font-size: 2rem;
    font-weight: 900;
    color: var(--accent);
    margin: 0 0 10px 0;
  }

  span {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 2px;
    opacity: 0.8;
  }
  
  ul {
    padding-left: 15px;
    margin-top: 10px;
    li {
      font-size: 0.85rem;
      opacity: 0.8;
    }
  }
`;

export const ProcessSection = styled.div`
  margin-bottom: 120px;

  .grid-3 {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 40px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

export const ProcessCol = styled.div`
  color: var(--text-color);
  .icon {
    width: 40px;
    height: 40px;
    background: var(--panel-bg);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--accent);
    font-weight: bold;
    margin-bottom: 24px;
    border: 1px solid rgba(150,150,150,0.2);
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 800;
    margin-bottom: 16px;
  }

  p {
    font-size: 1rem;
    line-height: 1.6;
    opacity: 0.8;
    margin-bottom: 20px;
  }

  ul {
    padding-left: 0;
    list-style: none;
    
    li {
      position: relative;
      padding-left: 16px;
      font-size: 0.9rem;
      opacity: 0.7;
      margin-bottom: 8px;

      &::before {
        content: "•";
        color: var(--accent);
        position: absolute;
        left: 0;
      }
    }
  }
`;

export const SolutionGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 30px;
  margin-bottom: 120px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  .wide {
    grid-column: 1 / -1;
  }

  .pop-image {
    background: var(--panel-bg);
    padding: 20px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const ResultsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 80px;
  margin-bottom: 120px;
  color: var(--text-color);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  h2 {
    font-size: 2.5rem;
    font-weight: 900;
    margin: 0 0 20px 0;
    letter-spacing: -1px;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.6;
    opacity: 0.8;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .result-stat {
    h3 {
      font-size: 3.5rem;
      font-weight: 900;
      color: var(--accent);
      margin: 0 0 10px 0;
      letter-spacing: -2px;
    }
    h4 {
      font-size: 0.9rem;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin: 0 0 10px 0;
    }
    p {
      font-size: 0.9rem;
      opacity: 0.7;
    }
  }
`;
